import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Button, Card, message, Select, AutoComplete, Checkbox, Alert } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import apiService from '@/services/api.service.ts';
import dayjs from 'dayjs';

interface Federation {
    id: number;
    name: string;
    status: string;
}

interface Club {
    id: number;
    name: string;
    federationId: number;
    status: string;
}

export const Register: React.FC = () => {
    const navigate = useNavigate();
    const [selectedFederationId, setSelectedFederationId] = useState<number | undefined>(undefined);
    const [federationSearchValue, setFederationSearchValue] = useState<string>('');
    const [federationOptions, setFederationOptions] = useState<{ value: string; label: string; id?: number }[]>([]);
    const [clubSearchValue, setClubSearchValue] = useState<string>('');
    const [clubOptions, setClubOptions] = useState<{ value: string; label: string; id?: number }[]>([]);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const clubSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [form] = Form.useForm();
    const [rateLimitError, setRateLimitError] = useState<{ message: string; retryAfter?: string } | null>(null);

    // Поиск федераций при вводе текста
    const searchFederations = async (searchText: string) => {
        if (!searchText || searchText.trim().length < 2) {
            setFederationOptions([]);
            return;
        }

        try {
            const response: any = await apiService.get('/federations', {
                params: {
                    search: searchText.trim(),
                    status: 'active',
                    limit: 10
                }
            });

            const items = response.data.data?.items || response.data.data || [];
            const options = items.map((f: Federation) => ({
                value: f.name,
                label: f.name,
                id: f.id
            }));

            // Добавляем опцию для создания новой федерации, если точного совпадения нет
            const exactMatch = items.find((f: Federation) => 
                f.name.toLowerCase() === searchText.trim().toLowerCase()
            );
            
            if (!exactMatch && searchText.trim().length >= 2) {
                options.push({
                    value: `create:${searchText.trim()}`,
                    label: `Создать "${searchText.trim()}"`,
                    id: undefined
                });
            }

            setFederationOptions(options);
        } catch (error: any) {
            // Не показываем ошибку пользователю, просто очищаем опции
            setFederationOptions([]);
        }
    };

    const handleFederationSearch = (value: string) => {
        setFederationSearchValue(value);
        
        // Очищаем предыдущий таймаут
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Дебаунс поиска
        searchTimeoutRef.current = setTimeout(() => {
            searchFederations(value);
        }, 300);
    };

    const handleFederationSelect = async (value: string, option: any) => {
        // Если выбрано создание новой федерации
        if (value.startsWith('create:')) {
            const federationName = value.replace('create:', '');
            try {
                const response: any = await apiService.post('/federations/public', {
                    name: federationName
                });
                
                const newFederation = response.data.data;
                setSelectedFederationId(newFederation.id);
                form.setFieldsValue({ federationId: newFederation.id });
                setFederationSearchValue(newFederation.name);
                message.success(`Федерация "${newFederation.name}" создана`);
            } catch (error: any) {
                const errorMessage = error?.response?.data?.error || error?.message || 'Ошибка при создании федерации';
                message.error(errorMessage);
            }
        } else {
            // Выбрана существующая федерация
            const federationId = option?.id;
            // Используем label из option, если он есть, иначе value (но value должно быть строкой - названием)
            const federationName = option?.label || (typeof value === 'string' ? value : '');
            if (federationId) {
                setSelectedFederationId(federationId);
                form.setFieldsValue({ federationId });
                setFederationSearchValue(federationName);
            }
        }
    };

    // Поиск клубов при вводе текста
    const searchClubs = async (searchText: string) => {
        if (!searchText || searchText.trim().length < 2) {
            setClubOptions([]);
            return;
        }

        try {
            const params: any = {
                status: 'active',
                limit: 1000
            };
            // Добавляем фильтр по федерации, только если она выбрана
            if (selectedFederationId) {
                params.federationId = selectedFederationId;
            }

            const response: any = await apiService.get('/clubs', { params });

            const items = response.data.data?.rows || response.data.data || [];
            const activeClubs = items.filter((c: Club) => c.status === 'active');
            
            // Фильтруем клубы по поисковому запросу на клиенте
            const filteredClubs = activeClubs.filter((c: Club) =>
                c.name.toLowerCase().includes(searchText.trim().toLowerCase())
            );

            const options = filteredClubs.map((c: Club) => ({
                value: c.name,
                label: c.name,
                id: c.id
            }));

            // Добавляем опцию для создания нового клуба, если точного совпадения нет
            const exactMatch = filteredClubs.find((c: Club) => 
                c.name.toLowerCase() === searchText.trim().toLowerCase()
            );
            
            if (!exactMatch && searchText.trim().length >= 2) {
                options.push({
                    value: `create:${searchText.trim()}`,
                    label: `Создать "${searchText.trim()}"`,
                    id: undefined
                });
            }

            setClubOptions(options);
        } catch (error: any) {
            // Не показываем ошибку пользователю, просто очищаем опции
            setClubOptions([]);
        }
    };

    const handleClubSearch = (value: string) => {
        setClubSearchValue(value);
        
        // Очищаем предыдущий таймаут
        if (clubSearchTimeoutRef.current) {
            clearTimeout(clubSearchTimeoutRef.current);
        }

        // Дебаунс поиска
        clubSearchTimeoutRef.current = setTimeout(() => {
            searchClubs(value);
        }, 300);
    };

    const handleClubSelect = async (value: string, option: any) => {
        // Если выбрано создание нового клуба
        if (value.startsWith('create:')) {
            const clubName = value.replace('create:', '');
            try {
                const requestData: any = { name: clubName };
                // Добавляем federationId только если она выбрана
                if (selectedFederationId) {
                    requestData.federationId = selectedFederationId;
                }
                const response: any = await apiService.post('/clubs/public', requestData);
                
                const newClub = response.data.data;
                form.setFieldsValue({ clubId: newClub.id });
                setClubSearchValue(newClub.name);
                message.success(`Клуб "${newClub.name}" создан`);
            } catch (error: any) {
                const errorMessage = error?.response?.data?.error || error?.message || 'Ошибка при создании клуба';
                message.error(errorMessage);
            }
        } else {
            // Выбран существующий клуб
            const clubId = option?.id;
            // Используем label из option, если он есть, иначе value (но value должно быть строкой - названием)
            const clubName = option?.label || (typeof value === 'string' ? value : '');
            if (clubId) {
                form.setFieldsValue({ clubId });
                setClubSearchValue(clubName);
            }
        }
    };

    // Убрали useEffect для загрузки клубов при изменении федерации,
    // так как теперь клубы загружаются только при поиске

    useEffect(() => {
        // Очищаем таймауты при размонтировании
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            if (clubSearchTimeoutRef.current) {
                clearTimeout(clubSearchTimeoutRef.current);
            }
        };
    }, []);

    const onFinish = async (values: any) => {
        try {
            setRateLimitError(null); // Сбрасываем ошибку rate limit при новой попытке
            const response: any = await apiService.post('/auth/register', values);
            const data = response?.data?.data;
            if (data?.pendingApproval) {
              message.success(response?.data?.message || 'Заявка на вступление в клуб отправлена. Ожидайте подтверждения менеджером клуба.');
            } else if (data?.invitationSent) {
              message.success(response?.data?.message || 'На указанный email отправлена ссылка для подтверждения присоединения к клубу');
            } else {
              message.success('Регистрация успешна! Пожалуйста, проверьте почту и подтвердите регистрацию.');
            }
            navigate('/login');
        } catch (error: any) {
            // Проверяем, является ли это ошибкой rate limit (429)
            if (error?.response?.status === 429) {
                const retryAfter = error?.response?.data?.retryAfter;
                const errorMessage = error?.response?.data?.message || 
                                   error?.response?.data?.error || 
                                   'Слишком много попыток регистрации. Пожалуйста, попробуйте позже.';
                
                let retryAfterText = '';
                if (retryAfter) {
                    try {
                        // Парсим retryAfter (может быть ISO строка или timestamp в секундах/миллисекундах)
                        let retryDate: dayjs.Dayjs | null = null;
                        
                        if (typeof retryAfter === 'string') {
                            // ISO строка
                            retryDate = dayjs(retryAfter);
                        } else if (typeof retryAfter === 'number') {
                            // Число - проверяем, это секунды или миллисекунды
                            // Если число меньше текущего timestamp в секундах, значит это секунды
                            const nowSeconds = Math.floor(Date.now() / 1000);
                            if (retryAfter < nowSeconds) {
                                // Это секунды
                                retryDate = dayjs(retryAfter * 1000);
                            } else if (retryAfter < nowSeconds * 1000) {
                                // Это миллисекунды
                                retryDate = dayjs(retryAfter);
                            } else {
                                // Это уже timestamp в миллисекундах
                                retryDate = dayjs(retryAfter);
                            }
                        }
                        
                        if (retryDate && retryDate.isValid()) {
                            const now = dayjs();
                            const diffMinutes = retryDate.diff(now, 'minute');
                            
                            if (diffMinutes > 0) {
                                retryAfterText = `Повторить попытку можно через ${diffMinutes} ${diffMinutes === 1 ? 'минуту' : diffMinutes < 5 ? 'минуты' : 'минут'} (после ${retryDate.format('HH:mm')}).`;
                            } else {
                                const diffSeconds = retryDate.diff(now, 'second');
                                if (diffSeconds > 0) {
                                    retryAfterText = `Повторить попытку можно через ${diffSeconds} ${diffSeconds === 1 ? 'секунду' : diffSeconds < 5 ? 'секунды' : 'секунд'}.`;
                                } else {
                                    retryAfterText = `Повторить попытку можно после ${retryDate.format('HH:mm')}.`;
                                }
                            }
                        }
                    } catch (parseError) {
                        // Если не удалось распарсить, просто показываем сообщение без времени
                    }
                }
                
                setRateLimitError({
                    message: errorMessage,
                    retryAfter: retryAfterText
                });
                
                // Также показываем сообщение через antd message
                message.error(retryAfterText ? `${errorMessage} ${retryAfterText}` : errorMessage);
            } else {
                // Для других ошибок просто показываем сообщение
                const errorMessage = error?.response?.data?.message || error?.message || 'Ошибка при регистрации';
                message.error(errorMessage);
                setRateLimitError(null);
            }
        }
    };

    return (
        <div style={{ maxWidth: 500, margin: '40px auto', padding: '0 16px' }}>
            <Card title="Регистрация">
                {rateLimitError && (
                    <Alert
                        message="Превышен лимит запросов"
                        description={
                            <div>
                                <div>{rateLimitError.message}</div>
                                {rateLimitError.retryAfter && (
                                    <div style={{ marginTop: 8, fontWeight: 500 }}>
                                        {rateLimitError.retryAfter}
                                    </div>
                                )}
                            </div>
                        }
                        type="warning"
                        showIcon
                        closable
                        onClose={() => setRateLimitError(null)}
                        style={{ marginBottom: 16 }}
                    />
                )}
                <Form
                    form={form}
                    name="register"
                    onFinish={onFinish}
                    layout="vertical"
                >
                    <Form.Item
                        name="email"
                        rules={[
                            { required: true, message: 'Введите email' },
                            { type: 'email', message: 'Введите корректный email' }
                        ]}
                    >
                        <Input 
                            prefix={<MailOutlined />} 
                            placeholder="Email" 
                        />
                    </Form.Item>

                    <Form.Item
                        name="lastName"
                        rules={[{ required: true, message: 'Введите фамилию' }]}
                    >
                        <Input 
                            prefix={<UserOutlined />} 
                            placeholder="Фамилия" 
                        />
                    </Form.Item>

                    <Form.Item
                        name="firstName"
                        rules={[{ required: true, message: 'Введите имя' }]}
                    >
                        <Input 
                            prefix={<UserOutlined />} 
                            placeholder="Имя" 
                        />
                    </Form.Item>

                    <Form.Item
                        name="federationId"
                        style={{ display: 'none' }}
                    >
                        <Input type="hidden" />
                    </Form.Item>
                    <Form.Item
                        label="Федерация"
                    >
                        <AutoComplete
                            options={federationOptions}
                            onSearch={handleFederationSearch}
                            onSelect={handleFederationSelect}
                            placeholder="Введите название федерации"
                            value={federationSearchValue}
                            onChange={setFederationSearchValue}
                            filterOption={false}
                            notFoundContent={federationSearchValue.length < 2 ? 'Введите минимум 2 символа для поиска' : null}
                        />
                    </Form.Item>

                    <Form.Item
                        name="clubId"
                        style={{ display: 'none' }}
                    >
                        <Input type="hidden" />
                    </Form.Item>
                    <Form.Item
                        label="Клуб"
                    >
                        <AutoComplete
                            options={clubOptions}
                            onSearch={handleClubSearch}
                            onSelect={handleClubSelect}
                            placeholder="Введите название клуба (необязательно)"
                            value={clubSearchValue}
                            onChange={setClubSearchValue}
                            filterOption={false}
                            notFoundContent={
                                clubSearchValue.length < 2 
                                    ? 'Введите минимум 2 символа для поиска' 
                                    : null
                            }
                        />
                    </Form.Item>

                    <Form.Item
                        name="role"
                        label="Роль"
                        rules={[{ required: true, message: 'Выберите роль' }]}
                        initialValue="participant"
                    >
                        <Select placeholder="Выберите роль">
                            <Select.Option value="participant">Участник</Select.Option>
                            <Select.Option value="coach">Тренер</Select.Option>
                            <Select.Option value="manager">Менеджер</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="password"
                        rules={[
                            { required: true, message: 'Введите пароль' },
                            { min: 8, message: 'Пароль должен содержать минимум 8 символов' },
                            {
                                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                                message: 'Пароль должен содержать заглавные и строчные буквы, цифры и специальные символы'
                            }
                        ]}
                    >
                        <Input.Password 
                            prefix={<LockOutlined />} 
                            placeholder="Пароль (мин. 8 символов, буквы, цифры, спецсимволы)" 
                        />
                    </Form.Item>

                    <Form.Item
                        name="personalDataConsent"
                        valuePropName="checked"
                        rules={[
                            {
                                validator: (_, value) =>
                                    value
                                        ? Promise.resolve()
                                        : Promise.reject(new Error('Необходимо дать согласие на обработку персональных данных'))
                            }
                        ]}
                    >
                        <Checkbox>
                            Я даю согласие на обработку персональных данных в соответствии с Федеральным законом от 27.07.2006 № 152-ФЗ «О персональных данных». С{' '}
                            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                                политикой конфиденциальности
                            </a>{' '}
                            ознакомлен(а).
                        </Checkbox>
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" block>
                            Зарегистрироваться
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};
